/**
 * O vocabulário e a aritmética das campanhas.
 *
 * Os dois adaptadores precisam do mesmo texto e das mesmas contas: é por este
 * rótulo que a clínica decide para quem manda mensagem, e por este número que
 * ela decide onde investir.
 *
 * **Custo por lead e retorno só existem com investimento declarado.** Nenhuma
 * plataforma de anúncio está integrada; o que a clínica gastou é ela quem
 * informa. Sem o valor, os dois campos ficam de FORA — zero diria "não custou
 * nada" no lugar de "ninguém informou".
 */

import type {
    Campanha, Indicador, LinhaDeOrigem, PublicoDaCampanha, PublicoDisponivel,
} from './tipos';

export interface DescricaoDePublico {
    rotulo: string;
    descricao: string;
}

export const PUBLICOS: Record<PublicoDaCampanha, DescricaoDePublico> = {
    'sem-retorno': {
        rotulo: 'Sem retorno',
        descricao: 'Pacientes atendidos há mais tempo que o limite e sem nada marcado',
    },
    'aniversariantes': {
        rotulo: 'Aniversariantes do mês',
        descricao: 'Pacientes que fazem aniversário no mês corrente',
    },
    'por-origem': {
        rotulo: 'Leads de uma origem',
        descricao: 'Quem chegou por um canal específico e ainda está no funil',
    },
    'todos-pacientes': {
        rotulo: 'Todos os pacientes',
        descricao: 'A carteira inteira, sem recorte',
    },
};

export const CANAIS_DA_CAMPANHA: Record<Campanha['canal'], string> = {
    whatsapp: 'WhatsApp',
    email: 'E-mail',
    instagram: 'Instagram',
};

export const STATUS_DA_CAMPANHA: Record<Campanha['status'], { rotulo: string; tom: 'success' | 'neutral' | 'warning' }> = {
    ativa: { rotulo: 'Ativa', tom: 'success' },
    encerrada: { rotulo: 'Encerrada', tom: 'neutral' },
    rascunho: { rotulo: 'Rascunho', tom: 'warning' },
};

/** Os números derivados da campanha, com as ausências preservadas. */
export function derivadosDaCampanha(base: {
    investimentoCentavos: number;
    leads: number;
    receitaCentavos: number;
}): Pick<Campanha, 'custoPorLeadCentavos' | 'retorno'> {
    if (!base.investimentoCentavos) return {};
    return {
        ...(base.leads ? { custoPorLeadCentavos: Math.round(base.investimentoCentavos / base.leads) } : {}),
        retorno: Math.round((base.receitaCentavos / base.investimentoCentavos) * 10) / 10,
    };
}

export function publicosDisponiveis(tamanhos: Record<PublicoDaCampanha, number>): PublicoDisponivel[] {
    return (Object.keys(PUBLICOS) as PublicoDaCampanha[]).map((tipo) => ({
        tipo,
        rotulo: PUBLICOS[tipo].rotulo,
        descricao: PUBLICOS[tipo].descricao,
        tamanho: tamanhos[tipo] ?? 0,
    }));
}

export function indicadoresDeMarketing(campanhas: Campanha[], origens: LinhaDeOrigem[]): Indicador[] {
    const ativas = campanhas.filter((c) => c.status === 'ativa');
    const leads = origens.reduce((s, o) => s + o.leads, 0);
    const investimento = campanhas.reduce((s, c) => s + c.investimentoCentavos, 0);
    const receita = origens.reduce((s, o) => s + o.receitaCentavos, 0);
    const alcance = ativas.reduce((s, c) => s + c.publicoTamanho, 0);

    const reais = (centavos: number) => `R$ ${(centavos / 100).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`;

    return [
        { id: 'leads', rotulo: 'Leads no mês', valor: String(leads), apoio: 'todas as origens', icone: 'target' },
        {
            id: 'alcance', rotulo: 'Alcance das ativas', valor: String(alcance),
            apoio: 'pessoas nas regras de público', icone: 'users',
        },
        {
            id: 'investimento', rotulo: 'Investimento declarado', valor: reais(investimento),
            apoio: investimento ? 'informado pela clínica' : 'nenhum valor informado', icone: 'circle-dollar-sign',
        },
        /* Receita atribuída pela ORIGEM do lead, que é o que os dados
           sustentam. Não é atribuição de anúncio — e a tela diz isso. */
        {
            id: 'receita', rotulo: 'Receita por origem', valor: reais(receita),
            apoio: 'leads ganhos no mês', tendencia: receita ? 'up' : 'neutral', icone: 'chart-no-axes-column',
        },
    ];
}
